import React, { useState } from 'react';
import axios from 'axios';
import { Phone, FileText, Stethoscope, ShieldCheck, Clock, CheckCircle2, Upload } from 'lucide-react';
import { CLINIC, IMAGES } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { icon: FileText, title: 'Confusing or conflicting reports', body: 'Make sense of complex blood tests and imaging with a clear, expert interpretation.' },
  { icon: Stethoscope, title: 'Uncertain diagnosis', body: 'Confirm whether your symptoms point to an autoimmune or rheumatic condition.' },
  { icon: ShieldCheck, title: 'Treatment review', body: 'Check that your current plan is optimal, safe and evidence-based.' },
  { icon: Clock, title: 'Before major decisions', body: 'Gain confidence before starting biologics, steroids or long-term therapy.' },
];

export default function SecondOpinion() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', condition: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/second-opinion`, { ...form, email: form.email.trim() || null });
      setSent(true);
      setForm({ name: '', phone: '', email: '', condition: '', message: '' });
      setTimeout(() => setSent(false), 6000);
    } catch (err) {
      setError('Something went wrong. Please call us instead — we will be happy to help.');
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHero eyebrow="Expert Review" title="Get a specialist second opinion" subtitle="A fresh, evidence-based review of your diagnosis, reports and treatment — bringing clarity and confidence to your care." image={IMAGES.consult} />

      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow="When it helps" title="Reasons to seek a second opinion" />
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={i * 80}>
                <div className="card-lift bg-white border border-teal-50 rounded-2xl p-7 h-full">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 mb-5"><r.icon size={26} /></span>
                  <h3 className="text-base text-teal-900 mb-2" style={{ fontWeight: 600 }}>{r.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mint-section py-20">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionTitle eyebrow="Request Now" title="Share your case with us" sub="Fill in your details and our team will call you back to arrange a review. We'll guide you on the reports to bring." />
            <div className="space-y-4">
              {['Reviewed by a UK-trained consultant', 'Clear written & verbal explanation', 'Practical, actionable next steps'].map((t) => (
                <div key={t} className="flex items-center gap-3 text-slate-700"><CheckCircle2 size={19} className="text-teal-600" /> {t}</div>
              ))}
            </div>
            <div className="mt-8 bg-white rounded-2xl border border-teal-50 p-5 flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700"><Phone size={22} /></span>
              <div>
                <div className="text-sm text-slate-500">Prefer to call?</div>
                <a href={`tel:${CLINIC.phoneRaw}`} className="font-semibold text-teal-800">{CLINIC.phone}</a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-8">
            {sent ? (
              <div className="text-center py-12">
                <span className="grid place-items-center w-16 h-16 rounded-full bg-teal-50 text-teal-600 mx-auto mb-4"><CheckCircle2 size={34} /></span>
                <h3 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>Request received!</h3>
                <p className="text-slate-600 mt-2">Our team will contact you shortly to arrange your second opinion.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                  <input data-testid="second-opinion-name-input" required value={form.name} onChange={set('name')} className="w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
                  <input data-testid="second-opinion-phone-input" required value={form.phone} onChange={set('phone')} className="w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition" placeholder="Mobile number" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email <span className="text-slate-400">(optional)</span></label>
                  <input data-testid="second-opinion-email-input" type="email" value={form.email} onChange={set('email')} className="w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition" placeholder="For a confirmation email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Condition / Concern</label>
                  <input value={form.condition} onChange={set('condition')} className="w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition" placeholder="e.g. Rheumatoid arthritis, joint pain" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
                  <textarea rows={4} value={form.message} onChange={set('message')} className="w-full px-4 py-3 rounded-xl border border-teal-100 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition resize-none" placeholder="Briefly describe your symptoms and existing reports" />
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400"><Upload size={14} /> You can bring reports & imaging to your appointment.</div>
                {error && <p className="text-sm text-center" style={{ color: 'var(--coral-dark)' }}>{error}</p>}
                <button data-testid="second-opinion-submit-button" type="submit" disabled={loading} className="btn-coral w-full py-3.5 rounded-full font-semibold disabled:opacity-70">{loading ? 'Sending...' : 'Request Second Opinion'}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
